import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";
import type { CarConfigLabelType } from "../../utils/labels";
import { carConfigLabels } from "../../utils/labels";
import type { CTAConfigType } from "../../utils/types/AceConfigType";
import type { BusinessLeasingConfigType } from "../../../../shared-logic/ace/types";
import type {
    ChargingType,
    ServiceType,
    FinanceCalculatorEnvironmentType,
} from "../../../../../common-deprecated/types/SettingsType";
import type {
    BusinessFinanceSettingsType,
    FinanceSettingsType,
    InsuranceSettingsType,
} from "../../../../shared-logic/types/CommonSettingsType";
import {
    initialBusinessFinanceSettingsData,
    initialFinanceSettingsData,
    initialInsuranceSettingsData,
} from "../../../../shared-logic/types/CommonSettingsType";
import type { CheckoutModalsConfigType } from "../../server/data/formatCarConfigCheckoutModals";
import type { SummaryConfigType } from "../../utils/types/SummaryType";

export type CarConfigSettingsType = {
    initialized: boolean;
    loadSaveServer: string; // Setting which contains the CCSave URL
    enableExternalPrice: boolean; // Setting which enables/disables external price calculation
    enableIntegratedTradeIn: boolean; // Setting which enables the tradein on the application
    showAccessoriesRate: boolean; // Setting which shows triggers the logic to show monthly rates on configurables
    hideSidebar: boolean; // Setting which hides the sidebar showing the quick links/ccSave code
    monthlyRateService: string; // endpoint pointing to the finance monthly rate service
    monthlyRateUnselectedService: string; // Endpoint related to the configurables monthly rate
    monthlyRateSelectedService: string; // Endpoint related to the configurables monthly rate
    monthlyRateDisclaimerService: string; // Endpoint related to the finance disclaimer (CCGetLegalText)
    labels: Record<CarConfigLabelType, string>;
    insuranceRate: string; // Setting which contains the endpoint for insurance
    financeCalculatorUrl: string; // Setting which contains the endpoint being used for the finance calculator injection
    financeCalculatorEnvironment: FinanceCalculatorEnvironmentType; // Setting which contains the environment for the finance calculator
    insuranceCalculatorUrl: string; // endpoint related to the finance calculator injection. Contains the injection script
    financeAvailabilityService: string; // Endpoint which determines if the model has finance available.
    showETA: boolean; // Setting which determines the showing of the ETA
    forceCashPrice: boolean; // forces the total price to bne cash price on finance
    hideFinanceDetailsHeader: boolean; // Setting which hides the finance price and disclaimer from the header
    hideFinanceDetailsSummaryFooter: boolean; // Setting which hides the finance price and disclaimer from the sticky summary footer
    showFinanceDisclaimerInline: boolean; // Setting which ensures that the finance disclaimer is always shown in full, in stead of showing a button to go to the finance disclaimer.
    alwaysShowCashDisclaimer: boolean; // Setting which ensures that the cash price disclaimer is always shown BELOW the cash price.
    ctaConfigs?: CTAConfigType; // Ace configs for all CTA configuration
    summaryConfig?: SummaryConfigType;
    checkoutModalConfigs?: CheckoutModalsConfigType; // Ace configs for the checkout modals
    services?: ServiceType[]; // Ace config for the services section
    charging?: ChargingType[]; // Ace config for the public & home charging section
    taxFormats?: { title: string; key: string; disclaimer?: string }[]; // setting to determine the format for taxes and costs section
    businessLeasing?: BusinessLeasingConfigType;
    allowedCheckoutFinanceProduct: string; // Contains the productNames in which you can go to buy checkout flow with finance
    allowedCheckoutReserveProduct: string; // Contains the productNames in which you can go to checkout with reservation
    allowedCheckoutLeasingProduct: string; // Contains the productNames in which you can go to checkout with leasing
    ecoConsumptionCostService: string; // Setting to determine setting endpoint for the eco consumption costs section
    disableFinanceService: boolean; // Setting which disables the loading of the T1 finance service usefull for countries which do not have/want to show finance on motorizations
    hideTyreInfo: boolean; // Setting which will disable "more info" button on the wheels, thus not showing the sidepanel with tyre info
    loadedStepUrls?: Partial<{
        highlights: string;
        configure: string;
        specs: string;
    }>; // This is only populated when the carconfig is integrated
    disableAvailableInOtherGrades: boolean; // Setting which disables the available in other grades sections and fetchers.
    requireFinanceQuoteToDisplayFinanceData: boolean; // Setting which obligates a financeQuote to be present before displaying any finance data.
    disableTradeInForFinanceProducts?: string[]; // Trade-in data will become incompatible for every finance product added. When incompatible, no trade-in data will be sent to any finance endpoints.
    etaMatchingType: string; // Determines which cars are returned for ETA, see https://t1-preview-wiki.toyota-europe.com/index.php/CarConfig_Settings#summaryEtaMatchingType
    customStartingPriceFormatting: string; // This setting enables a different price formatting for the starting prices in the carconfig
    apheleiaHighlightsPageUrl?: string; // Setting which contains the URL to the highlights page
} & FinanceSettingsType &
    InsuranceSettingsType &
    BusinessFinanceSettingsType;

export const initialStateCarConfigSettings: CarConfigSettingsType = {
    initialized: false,
    labels: { ...carConfigLabels },
    loadSaveServer: "",
    enableExternalPrice: false,
    enableIntegratedTradeIn: false,
    showAccessoriesRate: false,
    hideSidebar: false,
    monthlyRateService: "", //"https://prev-toyota.finance.makolab.com/dispatcher/ccGetMonthlyPayment",
    monthlyRateUnselectedService: "", //"https://toyota.finance.makolab.com/dispatcher/api/GetMonthlyRates",
    monthlyRateSelectedService: "", //"https://toyota.finance.makolab.com/dispatcher/api/SaveAndPrint",
    monthlyRateDisclaimerService: "", //"https://prev-toyota.finance.makolab.com/dispatcher/ccGetLegalText",
    financeCalculatorUrl: "",
    insuranceCalculatorUrl: "",
    financeCalculatorEnvironment: undefined,
    allowedCheckoutFinanceProduct: "",
    allowedCheckoutReserveProduct: "",
    allowedCheckoutLeasingProduct: "",
    ecoConsumptionCostService: "",
    etaMatchingType: "",
    disableFinanceService: false,
    hideTyreInfo: false,
    ctaConfigs: undefined,
    showETA: false,
    forceCashPrice: false,
    hideFinanceDetailsHeader: false,
    hideFinanceDetailsSummaryFooter: false,
    showFinanceDisclaimerInline: false,
    alwaysShowCashDisclaimer: false,
    services: [],
    disableAvailableInOtherGrades: false,
    requireFinanceQuoteToDisplayFinanceData: false,
    customStartingPriceFormatting: "",
    ...initialFinanceSettingsData,
    ...initialInsuranceSettingsData,
    ...initialBusinessFinanceSettingsData,
};

export type CarConfigSettingsActionTypes = ReturnType<typeof initCarConfigSettings | typeof overrideCarConfigSettings>;

export const CarConfigSettingsSlice = createSlice({
    name: "carConfigSettings",
    initialState: initialStateCarConfigSettings,
    reducers: {
        initCarConfigSettings(state, action: PayloadAction<CarConfigSettingsType>) {
            return {
                ...state,
                ...action.payload,
                initialized: true,
            };
        },
        /**
         * This is used for the SEO improvements of the linking between the different carconfig steps. APH-407
         */
        overrideCarConfigSettings(state, action: PayloadAction<Pick<CarConfigSettingsType, "loadedStepUrls">>) {
            return {
                ...state,
                ...action.payload,
            };
        },
    },
});

export const { initCarConfigSettings, overrideCarConfigSettings } = CarConfigSettingsSlice.actions;
